/**
 * Las metas de un día con su etapa (Req. 18.10, 21.6, 21.7). Las lecturas son
 * puras y aptas para useLiveQuery: las usan Hoy, Comida y Plan.
 *
 * Regla acordada para todas las metas: un cambio aplica desde hoy (o después)
 * y los días anteriores conservan las suyas. Las metas sin plan (las de
 * Fernando) guardan su historial en los ajustes.
 */

import { db, actualizarSettings, asegurarSettings, leerSettings } from './schema';
import { enEscritura } from './tx';
import type { Plan, Settings } from './types';
import { esFechaLocal, type FechaLocal } from '@/domain/dates';
import { fechaLocalDe } from '@/domain/time';
import {
  avisoDeEtapaNueva,
  cambiarMetasManuales,
  metasDelDia,
  metasManualesVigentes,
  posicionEnPlan,
  type AvisoEtapaNueva,
  type HistorialMetasManuales,
  type MetasDelDia,
  type MetasManuales,
  type PosicionEnPlan,
} from '@/domain/etapas';
import { ErrorValidacion } from '@/domain/errors';

export interface MetasYEtapa {
  metas: MetasDelDia;
  posicion: PosicionEnPlan | null;
  /** El plan de la etapa que cubre el día. */
  plan: Plan | null;
}

/** Las metas sin plan de ahora y su historial, tal como están en los ajustes. */
export function historialMetasManualesDe(s: Settings): HistorialMetasManuales {
  return {
    actuales: {
      kcal: s.metaKcalManual,
      proteinaG: s.metaProteinaManualG,
      carbohidratosG: s.metaCarbohidratosManualG,
      grasaG: s.metaGrasaManualG,
      ayunoHoras: s.metaAyunoManualHoras,
    },
    desde: s.metasManualesDesde ?? null,
    anteriores: s.metasManualesAnteriores ?? [],
  };
}

export async function leerMetasDelDia(fecha: FechaLocal): Promise<MetasYEtapa> {
  const [settings, etapas, mantenimientos, planes] = await Promise.all([
    leerSettings(),
    db.stages.toArray(),
    db.maintenance.toArray(),
    db.plans.toArray(),
  ]);
  const manuales = metasManualesVigentes(historialMetasManualesDe(settings), fecha);
  const metas = metasDelDia(fecha, etapas, mantenimientos, manuales);
  const plan = metas.etapa === null ? null : (planes.find((p) => p.id === metas.etapa?.planId) ?? null);
  return { metas, posicion: posicionEnPlan(etapas, fecha), plan };
}

/**
 * El aviso de etapa nueva que corresponde mostrar hoy en Hoy (Req. 21.8), o
 * null. Solo de un plan activo. Si la fecha de creación del plan no se puede
 * leer, se avisa.
 */
export function avisoEtapaDeHoy(
  m: MetasYEtapa,
  ajustes: Pick<Settings, 'avisoEtapaVisto'>,
  hoy: FechaLocal,
): AvisoEtapaNueva | null {
  // Un plan terminado ya no anuncia etapas: Mi plan diría "Sin plan activo"
  if (m.plan !== null && m.plan.estado !== 'activo') return null;
  let creadoEl: FechaLocal | null = null;
  try {
    creadoEl = m.plan === null ? null : fechaLocalDe(m.plan.creadoEn);
  } catch {
    creadoEl = null;
  }
  return avisoDeEtapaNueva(m.posicion, creadoEl, hoy, ajustes.avisoEtapaVisto ?? null);
}

/** "Entendido" en el aviso de etapa nueva. ESCRIBE. */
export async function marcarAvisoEtapaVisto(clave: string): Promise<void> {
  await actualizarSettings({ avisoEtapaVisto: clave });
}

const valida = (v: number | null, min: number, max: number) =>
  v === null || (Number.isFinite(v) && v >= min && v <= max);

/**
 * Guarda las metas sin plan desde hoy. ESCRIBE. Las de antes quedan en el
 * historial hasta ayer, así los días anteriores conservan sus metas.
 */
export async function guardarMetasManuales(nuevas: MetasManuales, hoy: FechaLocal): Promise<void> {
  if (!esFechaLocal(hoy)) throw new ErrorValidacion('La fecha no es válida.');
  if (!valida(nuevas.kcal, 500, 10_000)) throw new ErrorValidacion('La meta de kcal debe estar entre 500 y 10,000.');
  if (![nuevas.proteinaG, nuevas.carbohidratosG, nuevas.grasaG].every((g) => valida(g, 0, 1000))) {
    throw new ErrorValidacion('Los gramos deben estar entre 0 y 1,000.');
  }
  if (!valida(nuevas.ayunoHoras, 0, 48)) throw new ErrorValidacion('Las horas de ayuno deben estar entre 0 y 48.');

  await asegurarSettings();
  await enEscritura([db.settings], async () => {
    const ajustes = await db.settings.get(1);
    if (ajustes === undefined) return;
    const cambio = cambiarMetasManuales(historialMetasManualesDe(ajustes), nuevas, hoy);
    if (cambio === null) return;
    await db.settings.update(1, {
      metaKcalManual: cambio.actuales.kcal,
      metaProteinaManualG: cambio.actuales.proteinaG,
      metaCarbohidratosManualG: cambio.actuales.carbohidratosG,
      metaGrasaManualG: cambio.actuales.grasaG,
      metaAyunoManualHoras: cambio.actuales.ayunoHoras,
      metasManualesDesde: cambio.desde,
      metasManualesAnteriores: [...cambio.anteriores],
    });
  });
}
